import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  AlertTriangle,
  Archive,
  CheckCircle2,
  Clock,
  Cpu,
  HardDrive,
  History,
  MemoryStick,
  Radar,
  Server,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { TimeSeriesChart } from '@/components/charts/TimeSeriesChart';
import { IconTile, MetricRow, Pill, SectionCard, StatusPill } from '@/components/panels/SectionCard';
import { PendingValue } from '@/components/surface/ui';
import { Alert } from '@/components/ui/alert';
import { Card } from '@/components/ui/card';
import {
  formatBytes,
  formatRate,
  formatUptime,
  metricsApi,
  platformOverviewKey,
} from '@/lib/metrics-api';
import { relativeTime } from '@/modules/monitoring/format';
import type { MetricPoint } from '@/types/api';

const WINDOWS = [
  { hours: 1, label: '1H' },
  { hours: 6, label: '6H' },
  { hours: 24, label: '24H' },
  { hours: 168, label: '7D' },
] as const;

/**
 * The operator's own platform — not a list of customers' servers.
 *
 * ## Why there is no customer server list here
 *
 * With five hundred customers that would be five hundred rows of somebody else's business, none of
 * it actionable. What an operator needs is whether **their** machine is healthy, whether the moving
 * parts are moving, and whether anything is failing across the fleet in a pattern that means the
 * *platform* broke.
 *
 * "Forty runs failed in the last hour" is a statement about a playbook, a package mirror or this
 * host's network, not about forty separate customers — and it is invisible from any individual
 * customer's dashboard.
 *
 * ## The Docker disk warning is not decoration
 *
 * Inside a container `df /` measures the image overlay, not the disk that fills up and stops the
 * database, the queue and every run log. An operator reassured by 4% while the real volume is full is exactly what the
 * warning prevents, and it is why `PLATFORM_DISK_PATH` exists.
 */
export function AdminOverviewPage() {
  const [hours, setHours] = useState<number>(1);

  const overview = useQuery({
    queryKey: platformOverviewKey(hours),
    queryFn: () => metricsApi.platformOverview(hours),
    // The platform host is sampled every minute — local reads, so they cost nothing.
    refetchInterval: 60_000,
  });

  const data = overview.data;
  const host = data?.host;
  const latest = host?.latest ?? null;
  const points = host?.points ?? [];

  const series = (pick: (p: MetricPoint) => number | null) =>
    points.map((p) => ({ at: p.at, value: pick(p) }));

  const services = data?.services;
  const fleet = data?.fleet;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div className="min-w-0">
          {/* "Dashboard", matching the sidebar. This IS the operator's dashboard — there is no
              other one for them, and calling it something else was what sent the owner looking
              for platform statistics on a tenant page named the thing they were looking for. */}
          <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
          <p className="text-sm text-muted-foreground">
            The machine this platform runs on, its moving parts, and what is happening across every
            customer.
          </p>
        </div>
        <div className="ml-auto flex gap-1">
          {WINDOWS.map((window) => (
            <button
              key={window.hours}
              type="button"
              onClick={() => setHours(window.hours)}
              className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                hours === window.hours
                  ? 'border-primary/40 bg-primary/10 text-primary'
                  : 'border-transparent text-muted-foreground hover:bg-muted/60'
              }`}
            >
              {window.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Things that mean the platform itself broke ───────────────────── */}
      {services !== undefined && fleet !== undefined && (
        <PlatformAlerts
          overdue={services.overdue}
          failing={services.failing}
          failedRuns={fleet.runs_failed_1h}
          unreachable={fleet.servers_unreachable}
        />
      )}

      {host?.disk_is_container_local === true && (
        <Alert variant="warning">
          <AlertTriangle className="size-4" />
          The disk figure below is this container&rsquo;s own filesystem, not the host&rsquo;s.
          Mount the host root read-only and set <code>PLATFORM_DISK_PATH</code> to it. Otherwise
          the real disk can fill up while this reads comfortably.
        </Alert>
      )}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
        <SectionCard
          title="Resource Usage"
          subtitle="This platform's own host"
          action={<Activity className="size-5 text-primary" />}
        >
          {host?.available !== true ? (
            <p className="text-sm text-muted-foreground">
              No readings yet. The platform samples its own host every minute; CPU and network need
              a second reading before they can be worked out at all.
            </p>
          ) : (
            <>
              <div className="space-y-6">
                <MetricRow
                  icon={Cpu}
                  colour="text-blue-400"
                  strokeColour="rgb(96 165 250)"
                  label="CPU Usage"
                  description={latest?.cpu_cores ? `Average across ${latest.cpu_cores} cores` : 'Average across all cores'}
                  value={latest?.cpu_percent ?? null}
                  series={points.map((p) => p.cpu_percent)}
                  max={100}
                />
                <MetricRow
                  icon={MemoryStick}
                  colour="text-purple-400"
                  strokeColour="rgb(192 132 252)"
                  label="Memory Usage"
                  description={
                    latest === null
                      ? 'RAM utilisation'
                      : `${formatBytes(latest.memory_used_bytes)} of ${formatBytes(latest.memory_total_bytes)}`
                  }
                  value={latest?.memory_percent ?? null}
                  series={points.map((p) => p.memory_percent)}
                  max={100}
                />
                <MetricRow
                  icon={HardDrive}
                  colour="text-amber-400"
                  strokeColour="rgb(34 211 238)"
                  label="Disk Usage"
                  description={
                    latest === null
                      ? 'Storage consumption'
                      : `${formatBytes(latest.disk_used_bytes)} of ${formatBytes(latest.disk_total_bytes)}`
                  }
                  value={latest?.disk_percent ?? null}
                  series={points.map((p) => p.disk_percent)}
                  max={100}
                />
              </div>

              {latest !== null && (
                <div className="mt-6 grid grid-cols-2 gap-4 border-t pt-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Load per core</p>
                    <p className="font-semibold tabular-nums">{latest.load_per_core}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Uptime</p>
                    <p className="font-semibold tabular-nums">
                      {formatUptime(latest.uptime_seconds)}
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </SectionCard>

        <SectionCard
          title="Network Traffic"
          subtitle="Throughput, excluding loopback and container bridges"
          action={<Pill>{points.length} readings</Pill>}
        >
          <TimeSeriesChart
            series={[
              { label: 'In', color: 'rgb(96 165 250)', points: series((p) => p.rx_bytes_per_sec) },
              { label: 'Out', color: 'rgb(52 211 153)', points: series((p) => p.tx_bytes_per_sec) },
            ]}
            formatValue={(v) => formatRate(Math.round(v)).replace('/s', '')}
            emptyMessage="No network readings yet. Rates need two samples a minute apart."
          />
        </SectionCard>
      </div>

      {/* ── Business ──────────────────────────────────────────────────────── */}
      {/* Six across only where each tile has room for a two-word label on one line; at laptop
          widths six squeezed "Active subscriptions" and "Backup sources" onto two. */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 min-[1800px]:grid-cols-6">
        <Count label="Customers" value={data?.platform.customers} icon={Users} />
        <Count
          label="Active subscriptions"
          value={data?.platform.active_subscriptions}
          icon={CheckCircle2}
        />
        <Count label="Servers managed" value={data?.platform.servers} icon={Server} />
        <Count label="Runs, 7 days" value={data?.platform.runs_7d} icon={History} />
        <Count label="Monitors" value={data?.platform.monitors} icon={Radar} />
        <Count label="Backup sources" value={data?.platform.backup_sources} icon={Archive} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* ── The moving parts ────────────────────────────────────────────── */}
        <SectionCard
          title="Background work"
          subtitle="Every sweep customers rely on without watching"
          action={
            services === undefined ? undefined : (
              <Pill tone={services.overdue + services.failing === 0 ? 'success' : 'danger'}>
                {services.scheduled_tasks.length} tasks
              </Pill>
            )
          }
        >
          <div className="space-y-2">
            {services?.scheduled_tasks.map((task) => (
              <div
                key={task.task}
                className="flex flex-wrap items-center justify-between gap-2 border-b py-2 text-sm last:border-0"
              >
                <div className="min-w-0">
                  <p className="font-mono text-xs font-medium">{task.task}</p>
                  <p className="truncate text-xs text-muted-foreground">
                    {task.last_run_at === null
                      ? 'has never run'
                      : `ran ${relativeTime(task.last_run_at)}`}
                    {task.last_summary !== '' && ` · ${task.last_summary}`}
                  </p>
                </div>
                <StatusPill
                  label={
                    task.overdue ? 'Overdue' : task.last_succeeded === false ? 'Failing' : 'OK'
                  }
                  tone={task.overdue ? 'danger' : task.last_succeeded === false ? 'warning' : 'success'}
                />
              </div>
            ))}
          </div>
        </SectionCard>

        {/* ── Fleet signals ───────────────────────────────────────────────── */}
        <SectionCard
          title="Across all customers"
          subtitle="Patterns here usually mean the platform broke, not one customer"
        >
          <div className="grid grid-cols-2 gap-4 text-sm">
            <Figure
              label="Runs finished, 24h"
              value={fleet === undefined ? undefined : String(fleet.runs_24h)}
              hint={
                // The loading case has to come first. `fleet?.x` is `undefined` before the
                // response lands, which is not `null`, so folding the two together printed a
                // literal "undefined% succeeded" for as long as the request was in flight.
                fleet === undefined
                  ? undefined
                  : fleet.run_success_rate === null
                    ? 'none finished'
                    : `${fleet.run_success_rate}% succeeded on every server`
              }
              alarming={
                fleet !== undefined &&
                fleet.run_success_rate !== null &&
                fleet.run_success_rate < 80
              }
            />
            <Figure
              label="Runs failed, last hour"
              value={fleet === undefined ? undefined : String(fleet.runs_failed_1h)}
              hint="a burst usually means one playbook or mirror broke"
              alarming={fleet !== undefined && fleet.runs_failed_1h >= 5}
            />
            <Figure
              label="Monitors down"
              value={fleet === undefined ? undefined : String(fleet.monitors_down)}
              hint={fleet === undefined ? undefined : `${fleet.open_incidents} open incidents`}
              alarming={fleet !== undefined && fleet.monitors_down > 0}
            />
            <Figure
              label="Servers unreachable"
              value={fleet === undefined ? undefined : String(fleet.servers_unreachable)}
              hint="offline or not answering"
              alarming={fleet !== undefined && fleet.servers_unreachable > 0}
            />
            <Figure
              label="Backups failed, 24h"
              value={fleet === undefined ? undefined : String(fleet.backups_failed_24h)}
              hint="across every customer"
              alarming={fleet !== undefined && fleet.backups_failed_24h > 0}
            />
            <Figure
              label="Runs not finished"
              value={fleet === undefined ? undefined : String(fleet.runs_active)}
              hint="running, queued or waiting for confirmation"
              alarming={false}
            />
          </div>
        </SectionCard>
      </div>
    </div>
  );
}

/**
 * Only the signals that mean the operator has a problem.
 *
 * Renders nothing when everything is fine — a banner that is always there is a banner nobody reads.
 */
function PlatformAlerts({
  overdue,
  failing,
  failedRuns,
  unreachable,
}: {
  overdue: number;
  failing: number;
  failedRuns: number;
  unreachable: number;
}) {
  const messages: string[] = [];

  if (overdue > 0) {
    messages.push(
      `${overdue} background task${overdue === 1 ? ' is' : 's are'} overdue. The scheduler may have stopped.`,
    );
  }

  if (failing > 0) {
    messages.push(`${failing} background task${failing === 1 ? '' : 's'} failed on its last run.`);
  }

  if (failedRuns >= 5) {
    messages.push(
      `${failedRuns} runs failed in the last hour. If they share a playbook, that playbook or its package source is the likely cause, not the customers.`,
    );
  }

  if (unreachable >= 3) {
    messages.push(
      `${unreachable} customer servers are unreachable. If that number jumped, check egress from this host.`,
    );
  }

  if (messages.length === 0) {
    return null;
  }

  return (
    <Alert variant="danger">
      <AlertTriangle className="size-4" />
      <ul className="space-y-1">
        {messages.map((message) => (
          <li key={message}>{message}</li>
        ))}
      </ul>
    </Alert>
  );
}

function Count({
  label,
  value,
  icon,
}: {
  label: string;
  value: number | undefined;
  icon: typeof Users;
}) {
  return (
    <Card className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-muted-foreground">{label}</p>
          {/* A pulse until the query succeeds, never a zero, which would read as "no customers". */}
          <p className="mt-1 text-2xl font-semibold tabular-nums">{value ?? <PendingValue />}</p>
        </div>
        <IconTile icon={icon} colour="text-blue-400" />
      </div>
    </Card>
  );
}

function Figure({
  label,
  value,
  hint,
  alarming,
}: {
  label: string;
  value: string | undefined;
  hint: string | undefined;
  alarming: boolean;
}) {
  return (
    <div>
      <p className="text-muted-foreground">{label}</p>
      <p className={`text-xl font-semibold tabular-nums ${alarming ? 'text-danger' : ''}`}>
        {value ?? <PendingValue />}
      </p>
      {hint !== undefined && (
        <p className="flex items-center gap-1 text-xs text-muted-foreground">
          <Clock className="size-3" />
          {hint}
        </p>
      )}
    </div>
  );
}
